import { Component, Inject, Injector, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, catchError, combineLatest, filter, of, switchMap, tap, throwError } from 'rxjs';
import { MessageService } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { APP_BASE_HREF } from '@angular/common';

import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { DataConnectorTypesEnum } from '../../../api/externalData/enums/data-connector-types.enum';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { DataConnectorModel } from '../../../Core/Users/models/data-connector.model';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { NewDataConnectionDialogComponent } from '../NewDataConnectionDialog/new-data-connection-dialog.component';
import { ConfigService } from '../../../Core/Config/services/config.service';
import { TextInputDialogComponent } from '../../../Common/Dialogs/components/TextInputDialog/text-input-dialog.component';
import { DataConnectionService } from '../../../Core/Users/services/data-connection.service';
import { DataConnectionCardModel } from './model/data-connection-card.model';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../Core/Dialogs/enums/overlay-animation.enum';
import { AgprComponentStyleEnum } from '../../../Common/Basic/enums/social-analysis-types.enum';
import { EmailAccountConnectorApi } from '../../../api/emailAccountConnector/models/email-account-connector.api';
import { CommonApiErrorEnum } from '../../../api/genericApi/enums/common-api-error.enum';
import { EmailAccountConnectorComponent, NylasConnectionResult, NylasConnectionResultType } from '../EmailAccountConnector/email-account-connector.component';
import { EmailAccountConnectorApiService } from '@api/emailAccountConnector/email-account-connector.api.service';

export const connectionTypeToLocalizationKey = {
    [DataConnectorTypesEnum.GOOGLE_ANALYTICS]: 'DataConnector.googleAnalytics.3',
    [DataConnectorTypesEnum.GOOGLE_ANALYTICS_4]: 'DataConnector.googleAnalytics.4',
    [DataConnectorTypesEnum.TWITTER]: 'DataConnector.twitter',
    [DataConnectorTypesEnum.INSTAGRAM]: 'DataConnector.instagram',
    [DataConnectorTypesEnum.FACEBOOK]: 'DataConnector.facebook',
    [DataConnectorTypesEnum.YOUTUBE]: 'DataConnector.youtube',
    [DataConnectorTypesEnum.REDDIT]: 'DataConnector.reddit',
    [DataConnectorTypesEnum.TIKTOK]: 'DataConnector.tiktok',
    [DataConnectorTypesEnum.LINKEDIN]: 'DataConnector.linkedin',
    [DataConnectorTypesEnum.EMAIL_ACCOUNT]: 'DataConnector.emailAccount',
    [DataConnectorTypesEnum.BLUESKY]: 'DataConnector.bluesky',
};

export const connectionTypeToIcon = {
    [DataConnectorTypesEnum.GOOGLE_ANALYTICS]: 'assets/images/google-analytics-3_logo.png',
    [DataConnectorTypesEnum.GOOGLE_ANALYTICS_4]: 'assets/images/google-analytics-4_logo.png',
    [DataConnectorTypesEnum.TWITTER]: 'assets/images/twitter_logo.png',
    [DataConnectorTypesEnum.INSTAGRAM]: 'assets/images/instagram_logo.png',
    [DataConnectorTypesEnum.FACEBOOK]: 'assets/images/facebook_logo.png',
    [DataConnectorTypesEnum.YOUTUBE]: 'assets/images/youtube_logo.png',
    [DataConnectorTypesEnum.REDDIT]: 'assets/images/reddit_logo.png',
    [DataConnectorTypesEnum.TIKTOK]: 'assets/images/tiktok_logo.png',
    [DataConnectorTypesEnum.LINKEDIN]: '',
    [DataConnectorTypesEnum.BLUESKY]: '',

};

export const socialConnectionPlaceholderImage = {
    [DataConnectorTypesEnum.TWITTER]: 'assets/images/placeholder-twitter.svg',
    [DataConnectorTypesEnum.INSTAGRAM]: 'assets/images/placeholder-instagram.svg',
    [DataConnectorTypesEnum.FACEBOOK]: 'assets/images/placeholder-facebook.svg',
    [DataConnectorTypesEnum.YOUTUBE]: 'assets/images/placeholder-youtube.svg',
    [DataConnectorTypesEnum.REDDIT]: 'assets/images/placeholder-reddit.svg',
    // TODO we dont have placeholders for these so just using the user placeholder for now
    [DataConnectorTypesEnum.TIKTOK]: 'assets/images/placeholder-user-small.svg',
    [DataConnectorTypesEnum.LINKEDIN]: 'assets/images/placeholder-user-small.svg',
    [DataConnectorTypesEnum.BLUESKY]: 'assets/images/placeholder-user-small.svg',

};

@Component({
    selector: 'agpr-data-connector',
    styleUrls: ['./data-connector.component.scss'],
    templateUrl: './data-connector.component.html',
    standalone: false
})
export class DataConnectorComponent implements OnInit, OnDestroy {
    readonly connectionTypes = DataConnectorTypesEnum;
    readonly buttonTypesEnum = ButtonTypesEnum;
    readonly connectionTypeToLocalizationKey = connectionTypeToLocalizationKey;
    readonly connectionTypeToIcon = connectionTypeToIcon;
    readonly socialConnectionPlaceholderImage = socialConnectionPlaceholderImage;
    readonly messageSeverityEnum = messageSeverityEnum;

    connections: DataConnectionCardModel[] = [];
    connectDataSourcesContactLink: string;

    emailAccountConnectors: EmailAccountConnectorApi[];

    isLoadingDataConnectors = true;

    connectionErrorList = [
        'COM-002',
        'COM-003',
    ];

    subscriptions: Subscription[] = [];
    dateFormat: string;

    private readonly animationTrigger = new BehaviorSubject(
        new FullProfileAnimationModel(
            FullProfileDialogAnimationsEnum.slideInOutAnimation,
            OverlayAnimationStateEnum.open
        )
    );

    private reauthenticateConnectionSubject = new Subject<{
        connectorCard: DataConnectionCardModel;
        code: string;
        state: string;
    }>();
    overlayContainer: Element;


    constructor(
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        private messageService: MessageService,
        private userStateService: CurrentUserStateService,
        private localizationService: LocalizationService,
        private coreDialogService: CoreDialogService,
        private titleService: Title,
        private configService: ConfigService,
        private dataConnectionService: DataConnectionService,
        private injector: Injector,
        private emailAccountConnectorApi: EmailAccountConnectorApiService
    ) {
        this.titleService.setTitle(
            this.localizationService.transform(
                'Account.dataConn.browserTitle'
            )
        );

        this.overlayContainer = document.getElementById('main-content');

        this.subscriptions.push(
            this.userStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => this.dateFormat = prefs.getDateFormatString()),
            combineLatest([
                this.dataConnectionService.loadingDataConnector$,
                this.dataConnectionService.loadingEmailAccountConnector$
            ]).subscribe(([loadingDataConnectors, loadingEmailAccountConnectors]) => {
                this.isLoadingDataConnectors = loadingDataConnectors || loadingEmailAccountConnectors;
            }),
            combineLatest([
                this.dataConnectionService.dataConnectors$,
                this.dataConnectionService.emailAccountConnectors$
            ]).subscribe(([dataConnectors, emailAccountConnectors]) => {
                this.connections = [];
                dataConnectors?.forEach(x => this.connections.push(new DataConnectionCardModel(x)));
                emailAccountConnectors?.forEach(x => this.connections.push(new DataConnectionCardModel(x)));
            }),
            this.reauthenticateConnectionSubject.pipe(
                switchMap(({connectorCard, code, state}) => this.emailAccountConnectorApi
                    .reAuthenticateEmailAccountConnector(connectorCard.emailAccountConnector, code, state).pipe(
                        tap(id => {
                            this.messageService.add({
                                severity: messageSeverityEnum.success,
                                summary: 'DataConnector.emailAccount.reauth.success.header',
                                detail: this.localizationService.transform(
                                    'DataConnector.emailAccount.reauth.success.message',
                                    { name: connectorCard.emailAccountConnector.connectorName }
                                )
                            });
                            connectorCard.loading = false;
                        }),
                        catchError(error => {
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: 'DataConnector.emailAccount.reauth.failed.header',
                                detail: (this.connectionErrorList.find(err => err === error?.error?.details[0]?.code)) ?
                                    this.localizationService.transform('DataConnector.error.' + error?.error?.details[0]?.code) :
                                    this.localizationService.transform(
                                        'DataConnector.connectionCouldNotBeEstablished',
                                        { name: connectorCard.emailAccountConnector.connectorName }
                                    )
                            });
                            connectorCard.loading = false;
                            return throwError(() => new Error(error));
                        })
                    ),
                )
            ).subscribe(() => this.dataConnectionService.refreshEmailAccountConnectors())
        );
    }

    ngOnInit(): void {
        this.setConnectDataSourcesContactLink();
    }

    openNewConnectionDialog() {
        const io = {
            inputs: {
                animationTrigger: this.animationTrigger,
                overlayContainer: this.overlayContainer,
                style: AgprComponentStyleEnum.social,
            },
            outputs: {
                dialogClose: (confirm: boolean) => {
                    if (confirm) {
                        this.refreshAllConnectors();
                    }
                }
            }
        };

        this.coreDialogService.open(NewDataConnectionDialogComponent, io, this.injector);
    }

    reauthenticateConnection(connection: DataConnectionCardModel) {
        const emailConnector = connection.emailAccountConnector;
        connection.loading = true;

        this.dataConnectionService.buildRedirectUrl(emailConnector)
            .pipe(
                catchError(error => {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'DataConnector.dataConnectionFailed',
                        detail: (this.connectionErrorList.find(err => err === error?.error?.details[0]?.code)) ?
                            this.localizationService.transform('DataConnector.error.' + error?.error?.details[0]?.code) :
                            this.localizationService.transform(
                                'DataConnector.connectionCouldNotBeEstablished',
                                { name: emailConnector?.connectorName }
                            )
                    });
                    connection.loading = false;
                    return of('');
                }))
            .subscribe((redirectUri) => {
                if (!redirectUri) {
                    connection.loading = false;
                    throw new Error('Could not retrieve nylas url');
                }

                const channel = new BroadcastChannel(EmailAccountConnectorComponent.MESSAGE_ID);
                const listener = e => {
                    if (e.origin !== window.location.origin || e.data.key !== EmailAccountConnectorComponent.MESSAGE_ID) {
                        return;
                    }

                    channel.removeEventListener('message', listener);
                    channel.close();

                    const result: NylasConnectionResult = e.data.result;
                    if (result.resultType === NylasConnectionResultType.authError) {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'DataConnector.dataConnectionFailed',
                            detail: this.localizationService.transform(
                                'DataConnector.connectionCouldNotBeEstablished',
                                { name: connection.emailAccountConnector.connectorName }
                            )
                        });
                        connection.loading = false;
                    } else {
                        this.reauthenticateConnectionSubject.next({
                            connectorCard: connection,
                            code: result.code,
                            state: result.state
                        });
                    }
                };
                channel.addEventListener ('message', listener);
                window.open(redirectUri);
            });
    }

    editConnection(connection: DataConnectionCardModel): void {
        this.coreDialogService.open(TextInputDialogComponent, {
            inputs: {
                id: 'dataConnector.editConnectionDialog',
                initialValue: connection.name,
                heading: 'DataConnector.editConnectionDialogHeading',
                description: 'DataConnector.dataProviderName',
                placeholder: 'DataConnector.enterDataProviderName',
                formValidator: (name: string) => {
                    if (this.connections.findIndex((existingConnection) => existingConnection.name === name && existingConnection !== connection) > -1) {
                        return [`DataConnector.error.${this.connectionErrorList[1]}`];
                    }

                    return [];
                },
                submit: (name: string) => {
                    connection.name = name;
                    this.updateDataConnector(connection);
                    return of(null);
                }
            }
        });
    }

    updateDataConnector(connection: DataConnectionCardModel): void {
        if (connection.type === DataConnectorTypesEnum.EMAIL_ACCOUNT) {
            connection.emailAccountConnector.connectorName = connection.name;
            this.dataConnectionService.updateEmailAccountConnector(connection.emailAccountConnector.id, connection.emailAccountConnector).pipe(
                catchError(error => {
                    this.message(messageSeverityEnum.error, connection.name, false, error?.error?.details[0]?.code);
                    this.refreshAllConnectors();
                    return of('error');
                })
            ).subscribe(res => {
                if (res !== 'error') {
                    this.message(messageSeverityEnum.success, connection.name, false);
                }
            });
        } else {
            connection.dataConnector.name = connection.name;
            this.dataConnectionService.patchDataConnector(connection.dataConnector.id, connection.dataConnector.toApi()).pipe(
                catchError(error => {
                    this.message(messageSeverityEnum.error, connection.name, false, error?.error?.details[0]?.code);
                    this.refreshAllConnectors();
                    return of('error');
                })
            ).subscribe(res => {
                if (res !== 'error') {
                    this.message(messageSeverityEnum.success, connection.name, false);
                }
            });
        }
    }

    deleteConnection(connection: DataConnectionCardModel): void {
        this.coreDialogService.open(ConfirmDialogComponent, {
            inputs: {
                id: 'dataConnector.deleteConnectionConfirmDialog',
                heading: 'DataConnector.deleteConnectionConfirmDialogHeading',
                alertMessage: 'DataConnector.deleteConnectionConfirmDialogAlertMessage',
                alertType: messageSeverityEnum.warning,
                message: this.localizationService.transform('DataConnector.deleteConnectionConfirmDialogAreYouSure', { name: connection.name }),
                okLabel: 'DataConnector.deleteConnectionConfirmDialogConfirm',
                confirmAction: () => {
                    if (connection.type === DataConnectorTypesEnum.EMAIL_ACCOUNT) {
                        this.dataConnectionService.deleteEmailAccountConnector(connection.emailAccountConnector.id).pipe(
                            catchError((x) => {
                                this.message(messageSeverityEnum.error, connection.name, true);
                                return of('error');
                            })
                        ).subscribe(res => {
                            if (res !== 'error') {
                                this.message(messageSeverityEnum.success, connection.name, true);
                                this.dataConnectionService.refreshEmailAccountConnectors();
                            }
                        });
                    } else {
                        this.dataConnectionService.deleteDataConnector(connection.dataConnector.id).pipe(
                            catchError((x) => {
                                this.message(messageSeverityEnum.error, connection.name, true);
                                return of('error');
                            })
                        ).subscribe(res => {
                            if (res !== 'error') {
                                this.message(messageSeverityEnum.success, connection.name, true);
                                this.dataConnectionService.refreshDataConnectors();
                            }
                        });
                    }

                    return of(null);
                }
            }
        });
    }

    private refreshAllConnectors() {
        this.dataConnectionService.refreshDataConnectors();
        this.dataConnectionService.refreshEmailAccountConnectors();
    }

    private message(severity: messageSeverityEnum, name: string, isDeleted?: boolean, errorCode?: CommonApiErrorEnum): void {
        let summary = isDeleted ? 'DataConnector.dataConnectionDeletedMessageSummary' : 'DataConnector.dataConnectionUpdated';
        let detail = isDeleted
            ? this.localizationService.transform('DataConnector.dataConnectionDeletedMessageDetail', { name })
            : this.localizationService.transform('DataConnector.dataConnectionUpdatedSuccessfully', { name});

        if (severity === messageSeverityEnum.error) {
            summary = isDeleted ? 'DataConnector.dataConnectionDeletedFailedMessageSummary' : 'DataConnector.dataConnectionFailed';
            detail = isDeleted
                ? this.localizationService.transform('DataConnector.dataConnectionDeletedFailedMessageDetail', { name })
                : this.localizationService.transform('DataConnector.dataConnectionFailedUpdate', { name});

            if (errorCode && this.connectionErrorList.find(err => err === errorCode)) {
                detail = this.localizationService.transform('DataConnector.error.' + errorCode);
            }
        }

        this.messageService.add(
            {
                severity,
                summary,
                detail
            }
        );
    }

    setConnectDataSourcesContactLink(): void {
        if (this.configService.brandGUIDIsBurrelles()) {
            this.connectDataSourcesContactLink = 'https://intercom.help/burrellesUS/en/articles/6387037-google-analytics-integration-premium';
        } else {
            this.connectDataSourcesContactLink = 'http://help.agilitypr.com/en/articles/5301399-how-to-connect-to-google-analytics';
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
